import { syncManager } from '../../../services/sync';
import { ollamaService } from '../../../services/ai/ollamaService';
import { db } from '../../../db';
import { LEGACY_CUSTOMER_STAGE_MAP, ALL_JOURNEY_STAGES } from './stageContent';

// ─── Exported Types ────────────────────────────────────────────────────────

export interface CommunicationEntry {
    date: string;
    timestamp: number;
    content: string;
    channel: string;
    type?: 'whatsapp' | 'email' | 'call' | 'video' | 'sms' | 'other';
    direction?: 'incoming' | 'outgoing' | 'system';
    taskData?: any;
}

export interface CJMSClient {
    id: string;
    name: string;
    email: string;
    phone?: string;
    source: 'lead' | 'customer';
    currentStage: string;
    sourceStage?: string;
    healthScore: number;
    riskLevel: string;
    lastCommunication: string;
    lastCommunicationContent?: string;
    lastCommunicationChannel?: string;
    nextAction: string;
    recommendation?: string;
    completedSteps?: string[];
    stepTimestamps?: Record<string, string>;
    resources?: string[];
    company?: string;
    notes?: string; // Added for AI context enrichment
    updatedAt?: string;

    // Lead-specific fields
    freedomNumber?: number;
    freedomNumberAchieved?: number;
    thirtyDayInceptionGoal?: number;
    thirtyDayInceptionAchieved?: number;
    starvingCrowdIdentified?: boolean;
    ruleOf100DailyGoal?: number;
    ruleOf100AchievedToday?: number;

    // Customer-specific fields
    quotes?: Array<{
        id: string;
        amount: number;
        date: string;
        status: 'draft' | 'sent' | 'accepted' | 'rejected';
        items?: Array<{description: string; quantity: number; price: number}>;
    }>;
    invoices?: Array<{
        id: string;
        amount: number;
        date: string;
        dueDate: string;
        status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
        paidAmount: number;
        outstandingAmount: number;
    }>;
    payments?: Array<{
        id: string;
        amount: number;
        date: string;
        method: string;
        invoiceId?: string;
    }>;
    refunds?: Array<{
        id: string;
        amount: number;
        date: string;
        reason: string;
    }>;
    credits?: Array<{
        id: string;
        amount: number;
        date: string;
        reason: string;
    }>;
    ltv?: number; // Lifetime Value
    purchasedProductsServices?: Array<{
        id: string;
        name: string;
        date: string;
        amount: number;
    }>;
    sharedFolderUrl?: string; // GitHub-based folder URL
    lastActive?: string;
    referredBy?: string;
    referrals?: Array<{
        id: string;
        name: string;
        email: string;
        date: string;
        status: 'contacted' | 'qualified' | 'converted';
    }>;
    partnerStatus?: 'none' | 'referral' | 'affiliate' | 'reseller' | 'strategic';
    partnerType?: string;
    commissionDue?: number;
    commissionEarnedToDate?: number;
    satisfactionScore?: number; // 1-5 scale
    testimonialReviewCaseStudy?: string; // URL or reference

    // Internal activities field for lead generation (structured for UI)
    internalActivities?: {
        freedomNumber: { goal: number; achieved: number; variance: number };
        thirtyDayInception: { goal: number; achieved: number; variance: number };
        starvingCrowd: boolean;
        ruleOf100: { dailyGoal: number; achievedToday: number; tracking: Record<string, number> };
    };
}

export interface CJMSAnalytics {
    stageDistribution: Record<string, number>;
    bottlenecks: string[];
    pipelineHealth: number;
}

// ─── Batch Communication Enrichment ──────────────────────────────────────────
// Replaces the N+1 pattern (updateClientLastCommunication per client) with a
// single Communications tab fetch + O(n) enrichment in one pass.

async function fetchAllCommunications(email?: string): Promise<any[]> {
    try {
        return await syncManager.fetchTabData('Communications', email || null, true);
    } catch {
        console.warn('[CJMS API] Failed to fetch Communications tab');
        return [];
    }
}

async function enrichClientsWithLatestCommunications(clients: CJMSClient[]): Promise<CJMSClient[]> {
    if (clients.length === 0) {return clients;}

    const allComms = await fetchAllCommunications();
    if (!Array.isArray(allComms) || allComms.length === 0) {return clients;}

    // Group communications by any normalized identifier for O(1) lookup.
    // The Hub's Communications row can store the client's phone (text, leading
    // apostrophe) in Customer_ID instead of an email; use that as a fallback key.
    const commsByKey = new Map<string, any[]>();
    for (const comm of allComms) {
        const emailKey = normalizeTextId(comm.Customer_Email || comm.Email || comm.email);
        const idKey = normalizeTextId(comm.Customer_ID);
        const keys = new Set<string>();
        if (emailKey) {keys.add(emailKey);}
        if (idKey && idKey !== emailKey) {keys.add(idKey);}
        if (keys.size === 0) {continue;}
        for (const key of keys) {
            if (!commsByKey.has(key)) {commsByKey.set(key, []);}
            commsByKey.get(key)!.push(comm);
        }
    }

    // Enrich each client with O(1) lookup into the grouped comms
    return clients.map(client => {
        const clientEmail = client.email.toLowerCase().trim();
        const clientPhoneDigits = String(client.phone || '').replace(/[^\d]/g, '');
        const idKey = normalizeTextId(client.id.replace(/^(customer_|lead_)/, ''));
        // Try email first, then normalized Client_ID, then phone-digits from the
        // Communications-row Customer_ID column (when the Hub stores phone there).
        let clientComms = commsByKey.get(clientEmail);
        if (!clientComms && idKey) {clientComms = commsByKey.get(idKey);}
        if (!clientComms && clientPhoneDigits) {
            clientComms = commsByKey.get(clientPhoneDigits);
        }
        if (!clientComms || clientComms.length === 0) {return client;}

        // Sort descending by timestamp, pick the latest
        clientComms.sort((a, b) => {
            const tsA = new Date(a.Timestamp || a.Date || 0).getTime();
            const tsB = new Date(b.Timestamp || b.Date || 0).getTime();
            return tsB - tsA;
        });

        const latest = clientComms[0];
        return {
            ...client,
            lastCommunication: formatRelativeDate(latest.Timestamp || latest.Date || ''),
            lastCommunicationContent: latest.Content || latest.Message || '',
            lastCommunicationChannel: latest.Channel || 'Unknown',
        };
    });
}

// ─── Constants ───────────────────────────────────────────────────────────────

export const CJMS_STAGES: string[] = ALL_JOURNEY_STAGES;

// ─── Helper: Format a date string as a relative label ────────────────────────

function formatRelativeDate(dateStr: string): string {
    const now = Date.now();
    const ts = new Date(dateStr).getTime();
    const diffMs = now - ts;

    if (isNaN(ts)) {return dateStr || 'N/A';}

    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) {return 'Just now';}
    if (diffMins < 60) {return `${diffMins}m ago`;}

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) {return `${diffHours}h ago`;}

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) {return `${diffDays}d ago`;}

    return new Date(dateStr).toLocaleDateString();
}

// ─── Helpers: Communication History (IndexedDB + Google Sheets) ────────────

// Normalize a WhatsApp JID (e.g. "27721234567@s.whatsapp.net") to a bare phone number.
function jidToPhone(jid: string): string {
    if (!jid) {return '';}
    return jid.split('@')[0].replace(/[^\d+]/g, '');
}

// Lowercase, trim, and strip Sheets "force-text" leading apostrophes from a cell.
// Used for identifier comparisons where the Hub may have stored the value as text.
function normalizeTextId(value: any): string {
    return String(value == null ? '' : value).replace(/^'+/, '').toLowerCase().trim();
}

// Extract a phone-like digits-only payload from any cell. Handles Sheets
// "force-text" leading apostrophes ('+27...) and the optional E.164 plus sign.
// Returns '' when the cell doesn't look like a phone, preserving existing
// Phone-column behaviour for non-phone identifiers.
function pickPhoneFromCell(value: any): string {
    const stripped = String(value == null ? '' : value).replace(/^'+/, '').replace(/[^+\d]/g, '');
    if (stripped.length >= 7 && /\d{7,}/.test(stripped)) {return stripped;}
    return '';
}

async function fetchLocalChatHistory(email: string, phone?: string): Promise<CommunicationEntry[]> {
    try {
        // chat_logs is keyed by sender/recipient JIDs, not email/phone columns.
        // Match rows whose sender or recipient JID normalizes to the client's phone.
        const targetPhone = String(phone || '').replace(/[^\d]/g, '');
        const allLogs = await (db as any).chat_logs.toArray();
        const chatLogs = (allLogs || []).filter((entry: any) => {
            if (!entry) {return false;}
            if (entry.email && entry.email === email) {return true;}
            if (entry.phone && String(entry.phone).replace(/[^\d]/g, '') === targetPhone) {return true;}
            if (targetPhone) {
                const senderPhone = jidToPhone(entry.sender || entry.from || '').replace(/[^\d]/g, '');
                const recipientPhone = jidToPhone(entry.recipient || entry.to || '').replace(/[^\d]/g, '');
                if (senderPhone && senderPhone === targetPhone) {return true;}
                if (recipientPhone && recipientPhone === targetPhone) {return true;}
            }
            return false;
        });

        return chatLogs.map((entry: any) => {
            const isOutgoing = entry.direction === 'outgoing'
                || (entry.sender && email && entry.sender === email)
                || (entry.from && entry.from === email);
            return {
                date: formatRelativeDate(entry.timestamp || entry.date || ''),
                timestamp: new Date(entry.timestamp || entry.date || '').getTime() || 0,
                content: entry.content || entry.message || entry.body || entry.text || '',
                channel: 'WhatsApp',
                type: 'whatsapp' as const,
                direction: (isOutgoing ? 'outgoing' : (entry.direction || 'incoming')) as 'incoming' | 'outgoing',
            };
        });
    } catch (err) {
        console.warn('[CJMS API] fetchLocalChatHistory error:', err);
        return [];
    }
}

async function syncGoWAMessagesToSheetOptimized(email: string, existingComms: any[], phone?: string): Promise<number> {
    let syncedCount = 0;
    try {
        const waMessages = await fetchLocalChatHistory(email, phone);
        console.log(`[Sync] Found ${waMessages.length} local WA messages for ${email}`);
        if (waMessages.length === 0) {return 0;}

        const existingKeys = new Set<string>();
        if (Array.isArray(existingComms)) {
            existingComms.forEach((c: any) => {
                const ts = new Date(c.Timestamp || c.Date || '').getTime();
                const key = `${c.Content || ''}:${ts}`;
                existingKeys.add(key);
            });
        }

        const logsToSync: any[] = [];
        for (const msg of waMessages) {
            const key = `${msg.content}:${msg.timestamp}`;
            if (existingKeys.has(key)) {continue;}

            const commId = `wa_sync_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
            logsToSync.push({
                item: 'Update',
                area: '',
                status: 'pending',
                timestamp: new Date(msg.timestamp).toISOString(),
                code: 'Communications',
                value: JSON.stringify({
                    Comm_ID: commId,
                    Customer_Name: email,
                    Customer_ID: email,
                    Channel: 'WhatsApp',
                    Timestamp: new Date(msg.timestamp).toISOString(),
                    Direction: msg.direction === 'outgoing' ? 'outgoing' : 'incoming',
                    Content: msg.content,
                    Message: msg.content,
                    Notes: 'Auto-synced from GoWA chat history',
                }),
                notes: '',
            });
            syncedCount++;
        }

        console.log(`[Sync] Identified ${syncedCount} new messages to sync`);
        if (logsToSync.length === 0) {return 0;}

        // Batch sync to avoid overwhelming the server
        const BATCH_SIZE = 50;
        for (let i = 0; i < logsToSync.length; i += BATCH_SIZE) {
            const batch = logsToSync.slice(i, i + BATCH_SIZE);
            await syncManager.sendSyncRequest(email, batch);
        }
    } catch (err) {
        console.warn('[CJMS API] Failed to sync GoWA messages:', err);
    }
    return syncedCount;
}

async function fetchCommunicationHistory(email: string, phone?: string, customerId?: string): Promise<CommunicationEntry[]> {
    const results: CommunicationEntry[] = [];

    try {
        const localChats = await fetchLocalChatHistory(email, phone);
        results.push(...localChats);

        const allComms = await syncManager.fetchTabData('Communications', email, true);
        if (Array.isArray(allComms)) {
            // Sync local GoWA messages to sheet in background to avoid blocking the UI
            syncGoWAMessagesToSheetOptimized(email, allComms, phone).catch(err =>
                console.warn('[CJMS API] Background sync failed:', err)
            );
        }

        const phoneDigits = String(phone || '').replace(/[^\d]/g, '');
        console.log(`[CJMS API] Filtering communications for Email: ${email}, Phone: ${phoneDigits}`);
        if (Array.isArray(allComms)) {
            console.log(`[CJMS API] Total rows in Communications tab: ${allComms.length}`);

            allComms.forEach((c, idx) => {
                const rowEmail = normalizeTextId(c.Customer_Email || c.Email || c.email);
                // Customer_ID is the most permissive identifier: it may hold an
                // email OR a phone (text-formatted with leading apostrophe in Sheets).
                const rowId = normalizeTextId(c.Customer_ID);
                const idAsPhone = pickPhoneFromCell(c.Customer_ID);
                const explicitPhone = pickPhoneFromCell(c.Phone || c.Mobile_Phone || c.Phone_Number || c.phone);
                const rowPhone = explicitPhone || idAsPhone;

                const emailLower = email.toLowerCase().trim();
                const customerIdLower = (customerId || '').toLowerCase().trim();
                const matchesEmail = !!email && (rowEmail === emailLower || rowId === emailLower);
                const matchesId = !!customerId && (rowId === customerIdLower);
                const matchesPhone = !!phoneDigits && !!rowPhone && (rowPhone === phoneDigits || rowPhone.endsWith(phoneDigits) || phoneDigits.endsWith(rowPhone));

                if (matchesEmail || matchesId || matchesPhone) {
                    console.log(`[CJMS API] MATCH FOUND at row ${idx}: ${rowEmail} / ${rowPhone} / ${rowId}`);
                }

                if (!matchesEmail && !matchesId && !matchesPhone) {return;}

                const taskData = (() => {
                    if (!c.Notes) {return undefined;}
                    try {
                        const parsed = JSON.parse(c.Notes);
                        return parsed.taskKey ? parsed : undefined;
                    } catch {
                        return undefined;
                    }
                })();

                results.push({
                    timestamp: new Date(c.Timestamp || c.Date || "").getTime() || 0,
                    date: formatRelativeDate(c.Timestamp || c.Date || ''),
                    content: c.Content || c.Message || c.Notes || '',
                    channel: c.Channel || 'Unknown',
                    type: (c.Channel || 'email').toLowerCase().includes('whatsapp')
                        ? 'whatsapp'
                        : (c.Channel || 'email').toLowerCase().includes('call')
                            ? 'call'
                            : 'other',
                    direction: (c.Direction || 'incoming') as 'incoming' | 'outgoing' | 'system',
                    taskData,
                });
            });
        }
    } catch (err) {
        console.warn('[CJMS API] fetchCommunicationHistory error:', err);
    }

    const seen = new Set<string>();
    const unique: CommunicationEntry[] = [];
    for (const entry of results) {
        const key = `${entry.content}:${entry.timestamp}:${entry.channel}`;
        if (!seen.has(key)) {
            seen.add(key);
            unique.push(entry);
        }
    }

    return unique.sort((a, b) => b.timestamp - a.timestamp);
}

async function fetchLastCommunication(email: string, phone?: string, customerId?: string): Promise<CommunicationEntry> {
    const history = await fetchCommunicationHistory(email, phone, customerId);
    if (history.length > 0) {
        return history[0];
    }
    return {
        date: 'N/A',
        content: '',
        channel: 'other',
        timestamp: 0
    };
}

async function updateClientLastCommunication(client: CJMSClient): Promise<CJMSClient> {
    if (!client.email) {return client;}
    try {
        const latest = await fetchLastCommunication(client.email, client.phone, client.id.replace(/^(customer_|lead_)/, ''));
        if (latest.content) {
            return {
                ...client,
                lastCommunication: latest.date,
                lastCommunicationContent: latest.content,
                lastCommunicationChannel: latest.channel,
            };
        }
    } catch (err) {
        console.warn('[CJMS API] Error updating last communication:', err);
    }
    return client;
}

const LEAD_STATUS_STAGE_MAP: Record<string, string> = {
    'New': 'Lead Generation',
    'Qualified': 'Lead Generation',
    'Converted': 'Lead Generation',
    'Lost': 'Lost',
};

function mapLeadStatusToStage(status: string): string {
    return LEAD_STATUS_STAGE_MAP[status] || 'Lead Generation';
}

function computeHealthScore(row: any): number {
    const valueScore = parseInt(row.Value_Score, 10);
    if (!isNaN(valueScore)) {return Math.min(100, Math.max(0, valueScore));}
    const sentiment = (row.Sentiment || '').toLowerCase();
    if (sentiment.includes('positive')) {return 80;}
    if (sentiment.includes('neutral')) {return 55;}
    if (sentiment.includes('negative') || sentiment.includes('at risk')) {return 30;}
    const contactDate = row.Last_Contact_Date || row.Last_Interaction || row.Last_Communication;
    if (contactDate) {
        const daysSince = Math.floor((Date.now() - new Date(contactDate).getTime()) / 86400000);
        if (daysSince < 7) {return 80;}
        if (daysSince < 30) {return 60;}
        if (daysSince < 90) {return 40;}
        return 20;
    }
    return 50;
}

function computeRiskLevel(score: number): string {
    if (score >= 70) {return 'low';}
    if (score >= 40) {return 'medium';}
    return 'high';
}

function mapCustomerRowToClient(row: any): CJMSClient {
    const rawStage = row.Lifecycle_Stage || row.Journey_Stage || '';
    const mappedStage = LEGACY_CUSTOMER_STAGE_MAP[rawStage] || rawStage || 'Lead Conversion';
    const healthScore = computeHealthScore(row);

    return {
        id: `customer_${row.Customer_ID || row.id || Math.random().toString(36).substr(2, 8)}`,
        name: row.Name || row.Customer_Name || row.Company || 'Unknown',
        email: row.Email || row.Customer_Email || '',
        phone: row.Phone || row.Mobile_Phone || '',
        source: 'customer',
        currentStage: mappedStage,
        sourceStage: rawStage,
        healthScore,
        riskLevel: computeRiskLevel(healthScore),
        lastCommunication: formatRelativeDate(row.Last_Interaction || row.Last_Communication || ''),
        nextAction: row.Next_Action || 'Schedule check-in',
        recommendation: row.Recommendation || '',
        resources: row.Resources ? (Array.isArray(row.Resources) ? row.Resources : row.Resources.split(',')) : [],
        updatedAt: row.Updated_At || row.Last_Interaction || '',
        notes: row.Notes || undefined,
    };
}

function getField(row: any, ...keys: string[]): string {
    for (const key of keys) {
        if (row[key]) {return String(row[key]);}
        const lower = key.toLowerCase();
        const matchKey = Object.keys(row).find(
            k => k.toLowerCase().replace(/[\s_-]/g, '') === lower.replace(/[\s_-]/g, '')
        );
        if (matchKey && row[matchKey]) {return String(row[matchKey]);}
    }
    return '';
}

function mapLeadRowToClient(row: any): CJMSClient {
    const status = getField(row, 'Status') || 'New';
    const stage = mapLeadStatusToStage(status);
    const healthScore = computeHealthScore(row);
    const fullName = [getField(row, 'First_Name', 'FirstName', 'First'), getField(row, 'Last_Name', 'LastName', 'Last')].filter(Boolean).join(' ').trim();
    const phone = getField(row, 'Phone', 'Mobile_Phone', 'MobilePhone', 'Phone_Number', 'PhoneNumber');
    const displayName = fullName
        || getField(row, 'Lead_Name', 'LeadName', 'Name', 'Full_Name', 'FullName')
        || getField(row, 'Company_Name', 'CompanyName', 'Company')
        || (phone ? `Lead ${phone}` : 'Unknown');

    return {
        id: `lead_${row.Lead_ID || row.id || Math.random().toString(36).substr(2, 8)}`,
        name: displayName,
        email: row.Email || '',
        phone: row.Phone || row.Mobile_Phone || '',
        source: 'lead',
        currentStage: stage,
        sourceStage: status,
        healthScore,
        riskLevel: computeRiskLevel(healthScore),
        lastCommunication: formatRelativeDate(row.Last_Contact_Date || ''),
        nextAction: row.Next_Followup_Date ? `Follow up by ${row.Next_Followup_Date}` : 'Initial outreach',
        recommendation: '',
        resources: row.Resources ? (Array.isArray(row.Resources) ? row.Resources : row.Resources.split(',')) : [],
        updatedAt: row.Last_Contact_Date || '',
        freedomNumber: parseFloat(row.Freedom_Number) || undefined,
        freedomNumberAchieved: parseFloat(row.Freedom_Number_Achieved) || undefined,
        thirtyDayInceptionGoal: parseFloat(row.Thirty_Day_Inception_Goal) || undefined,
        thirtyDayInceptionAchieved: parseFloat(row.Thirty_Day_Inception_Achieved) || undefined,
        starvingCrowdIdentified: row.Starving_Crowd_Identified === 'Yes' || row.Starving_Crowd_Identified === true,
        ruleOf100DailyGoal: parseInt(row.Rule_Of_100_Daily_Goal) || undefined,
        ruleOf100AchievedToday: parseInt(row.Rule_Of_100_Achieved_Today) || undefined,
        notes: row.Notes || undefined,
        company: row.Company || undefined,
    };
}

export const cjmsApi = {
    async syncCommunications(email: string, phone?: string): Promise<{ syncedCount: number, error?: string }> {
        try {
            const allComms = await syncManager.fetchTabData('Communications', email, true);
            const syncedCount = await syncGoWAMessagesToSheetOptimized(email, allComms, phone);
            return { syncedCount };
        } catch (err: any) {
            console.error('[CJMS API] Manual sync failed:', err);
            return { syncedCount: 0, error: err.message };
        }
    },

    async fetchCommunicationHistory(email: string, phone?: string, customerId?: string): Promise<CommunicationEntry[]> {
        return fetchCommunicationHistory(email, phone, customerId);
    },

    async fetchClients(): Promise<CJMSClient[]> {
        let customerRows: any[] = [];
        let leadRows: any[] = [];

        try {
            customerRows = await syncManager.fetchTabData('Customers', null, true);
        } catch {
            console.warn('[CJMS API] Failed to fetch Customers tab');
        }

        try {
            leadRows = await syncManager.fetchTabData('CRM_Leads', null, true);
        } catch {
            console.warn('[CJMS API] Failed to fetch CRM_Leads tab');
        }

        const customers = (Array.isArray(customerRows) ? customerRows : []).map(mapCustomerRowToClient);
        const leads = (Array.isArray(leadRows) ? leadRows : []).map(mapLeadRowToClient);

        const clients = [...customers, ...leads];

        return enrichClientsWithLatestCommunications(clients);
    },

    async fetchAnalytics(): Promise<CJMSAnalytics> {
        const clients = await this.fetchClients();
        const stageDistribution: Record<string, number> = {};
        for (const stage of CJMS_STAGES) {
            stageDistribution[stage] = 0;
        }
        for (const client of clients) {
            const stage = client.currentStage;
            stageDistribution[stage] = (stageDistribution[stage] || 0) + 1;
        }
        const total = clients.length;
        const bottlenecks: string[] = [];
        for (const [stage, count] of Object.entries(stageDistribution)) {
            if (total > 0 && (count / total) > 0.4) {
                bottlenecks.push(stage);
            }
        }
        const laterStageIndices = CJMS_STAGES.slice(2);
        const laterStageCount = clients.filter(c => laterStageIndices.includes(c.currentStage)).length;
        const pipelineHealth = total > 0 ? Math.round((laterStageCount / total) * 100) : 0;

        return {
            stageDistribution,
            bottlenecks,
            pipelineHealth,
        };
    },

    async addClient(data: {
        firstName: string;
        lastName: string;
        email: string;
        phone?: string;
        company?: string;
        notes?: string;
        source?: 'lead' | 'customer';
    }): Promise<{ success: boolean; error?: string }> {
        try {
            const timestamp = new Date().toISOString();
            const rowData = data.source === 'lead'
                ? {
                      First_Name: data.firstName,
                      Last_Name: data.lastName,
                      Email: data.email,
                      Phone: data.phone || '',
                      Mobile_Phone: data.phone || '',
                      Company: data.company || '',
                      Notes: data.notes || '',
                      Status: 'New',
                      Last_Contact_Date: timestamp,
                      Created_Date: timestamp,
                  }
                : {
                      Name: `${data.firstName} ${data.lastName}`.trim() || 'Unknown',
                      Customer_Name: `${data.firstName} ${data.lastName}`.trim() || 'Unknown',
                      Email: data.email,
                      Phone: data.phone || '',
                      Mobile_Phone: data.phone || '',
                      Company: data.company || '',
                      Notes: data.notes || '',
                      Lifecycle_Stage: 'Lead Conversion',
                      Journey_Stage: 'Lead Conversion',
                      Last_Interaction: timestamp,
                      Last_Contact_Date: timestamp,
                      Created_Date: timestamp,
                  };

            await syncManager.logAction(
                data.source === 'lead' ? 'CRM_Leads' : 'Customers',
                'add',
                rowData
            );

            await syncManager.logAction('Communications', 'add', {
                Comm_ID: `welcome_${Date.now()}`,
                Customer_Name: `${data.firstName} ${data.lastName}`.trim() || data.email,
                Customer_ID: data.email,
                Channel: 'other',
                Timestamp: timestamp,
                Direction: 'outgoing',
                Content: `Welcome ${data.source === 'lead' ? 'lead' : 'customer'} ${data.firstName}. ${data.notes || 'Looking forward to working together!'}`,
                Message: `Welcome ${data.firstName}! ${data.notes || 'We are excited to begin our journey together.'}`,
            });

            return { success: true };
        } catch (err: any) {
            console.error('[CJMS API] Failed to add client:', err);
            return { success: false, error: err.message || 'Failed to add client' };
        }
    },

    async setTaskCompletion(data: {
        email: string;
        stage: string;
        stepIndex: number;
        activityIndex?: number;
        taskType?: 'step' | 'activity';
        completed: boolean;
    }): Promise<{ success: boolean; error?: string }> {
        try {
            const taskKey = data.taskType === 'activity'
                ? `step-${data.stepIndex}-act-${data.activityIndex}`
                : `step-${data.stepIndex}`;
            const taskLabel = data.taskType === 'activity'
                ? `Activity ${(data.activityIndex ?? 0) + 1} under step ${data.stepIndex + 1}`
                : `Step ${data.stepIndex + 1}`;

            await syncManager.logAction('Communications', 'add', {
                Comm_ID: `task_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
                Customer_Name: data.email,
                Customer_ID: data.email,
                Channel: 'system',
                Timestamp: new Date().toISOString(),
                Direction: 'system',
                Content: data.completed
                    ? `✅ Completed ${taskLabel} in "${data.stage}"`
                    : `↩️ Unmarked ${taskLabel} in "${data.stage}"`,
                Message: data.completed
                    ? `${taskLabel} completed in ${data.stage} stage`
                    : `${taskLabel} unchecked in ${data.stage} stage`,
                Notes: JSON.stringify({
                    stage: data.stage,
                    stepIndex: data.stepIndex,
                    activityIndex: data.activityIndex,
                    taskType: data.taskType || 'step',
                    taskKey,
                    completed: data.completed,
                                timestamp: new Date().toISOString(),
                }),
            });
            return { success: true };
        } catch (err: any) {
            return { success: false, error: err.message || 'Failed to update task' };
        }
    },

    async getNextIncompleteTask(email: string, stage: string): Promise<string | null> {
        try {
            const communications = await syncManager.fetchTabData('Communications', email, false);
            if (!Array.isArray(communications)) {return null;}

            const taskEntries = communications.filter((c: any) =>
                c.Channel === 'system' &&
                c.Customer_ID === email &&
                c.Notes && c.Notes.includes(stage)
            );

            const completedKeys = new Set<string>();
            taskEntries.forEach((c: any) => {
                try {
                    const note = JSON.parse(c.Notes);
                    if (note.stage === stage && note.completed) {
                        completedKeys.add(note.taskKey || `step-${note.stepIndex}`);
                    }
                } catch {
                    // skip malformed entries
                }
            });

            const { getStageContent } = await import('./stageContent');
            const isLead = stage === 'Lead Generation';
            const content = getStageContent(stage, isLead);

            for (let i = 0; i < content.steps.length; i++) {
                const stepKey = `step-${i}`;
                if (!completedKeys.has(stepKey)) {
                    return content.steps[i];
                }
                const activityIndices = content.stepActivityMap?.[i] || [];
                for (const actIdx of activityIndices) {
                    const actKey = `step-${i}-act-${actIdx}`;
                    if (!completedKeys.has(actKey)) {
                        return content.activities[actIdx];
                    }
                }
            }
            return null;
        } catch {
            return null;
        }
    },

    async getNotes(email: string, phone?: string): Promise<string> {
        try {
            let clientData: any = null;
            const customers = await syncManager.fetchTabData('Customers', null, true);
            if (Array.isArray(customers)) {
                clientData = customers.find((r: any) =>
                    r.Email === email || r.Customer_Email === email ||
                    (phone && (r.Phone === phone || r.Mobile_Phone === phone))
                );
            }
            if (!clientData) {
                const leads = await syncManager.fetchTabData('CRM_Leads', null, true);
                if (Array.isArray(leads)) {
                    clientData = leads.find((r: any) =>
                        r.Email === email ||
                        (phone && (r.Phone === phone || r.Mobile_Phone === phone))
                    );
                }
            }
            return clientData?.Notes || '';
        } catch (err) {
            console.error('[CJMS API] Error fetching notes:', err);
            return '';
        }
    },

    async saveNotes(data: {
        email: string;
        phone?: string;
        notes: string;
    }): Promise<{ success: boolean; error?: string }> {
        try {
            const customers = await syncManager.fetchTabData('Customers', null, true);
            if (Array.isArray(customers)) {
                const customerIndex = customers.findIndex((r: any) =>
                    r.Email === data.email || r.Customer_Email === data.email ||
                    (data.phone && (r.Phone === data.phone || r.Mobile_Phone === data.phone))
                );
                if (customerIndex >= 0) {
                    await syncManager.logAction('Customers', 'update', {
                        ...customers[customerIndex],
                        Notes: data.notes,
                        Updated_At: new Date().toISOString(),
                    }, String(customerIndex));
                    return { success: true };
                }
            }
            const leads = await syncManager.fetchTabData('CRM_Leads', null, true);
            if (Array.isArray(leads)) {
                const leadIndex = leads.findIndex((r: any) =>
                    r.Email === data.email ||
                    (data.phone && (r.Phone === data.phone || r.Mobile_Phone === data.phone))
                );
                if (leadIndex >= 0) {
                    await syncManager.logAction('CRM_Leads', 'update', {
                        ...leads[leadIndex],
                        Notes: data.notes,
                        Updated_At: new Date().toISOString(),
                    }, String(leadIndex));
                    return { success: true };
                }
            }
            return { success: false, error: 'Client not found' };
        } catch (err: any) {
            return { success: false, error: err.message || 'Failed to save notes' };
        }
    },

    async getInternalActivities(email: string): Promise<{
        freedomNumber: { goal: number; achieved: number; variance: number };
        thirtyDayInception: { goal: number; achieved: number; variance: number };
        starvingCrowd: boolean;
        ruleOf100: { dailyGoal: number; achievedToday: number; tracking: Record<string, number> };
    }> {
        try {
            const clients = await this.fetchClients();
            const client = clients.find(c => c.email === email);
            if (!client) {return this.getDefaultInternalActivities();}

            return {
                freedomNumber: {
                    goal: client.freedomNumber || 0,
                    achieved: client.freedomNumberAchieved || 0,
                    variance: (client.freedomNumber || 0) - (client.freedomNumberAchieved || 0)
                },
                thirtyDayInception: {
                    goal: client.thirtyDayInceptionGoal || 10,
                    achieved: client.thirtyDayInceptionAchieved || 0,
                    variance: (client.thirtyDayInceptionGoal || 10) - (client.thirtyDayInceptionAchieved || 0)
                },
                starvingCrowd: client.starvingCrowdIdentified || false,
                ruleOf100: {
                    dailyGoal: client.ruleOf100DailyGoal || 100,
                    achievedToday: client.ruleOf100AchievedToday || 0,
                    tracking: {}
                }
            };
        } catch (err) {
            console.error('[CJMS API] Error fetching internal activities:', err);
            return this.getDefaultInternalActivities();
        }
    },

    getDefaultInternalActivities() {
        return {
            freedomNumber: { goal: 0, achieved: 0, variance: 0 },
            thirtyDayInception: { goal: 10, achieved: 0, variance: -10 },
            starvingCrowd: false,
            ruleOf100: {
                dailyGoal: 100,
                achievedToday: 0,
                tracking: {
                    monday: 0, tuesday: 0, wednesday: 0, thursday: 0, friday: 0
                }
            }
        };
    },

    async saveInternalActivities(email: string, data: {
        freedomNumber?: { goal: number; achieved: number };
        thirtyDayInception?: { goal: number; achieved: number };
        starvingCrowd?: boolean;
        ruleOf100?: { dailyGoal?: number; achievedToday?: number };
    }): Promise<{ success: boolean; error?: string }> {
        try {
            const updatedDetails: any = { email };
            if (data.freedomNumber) {
                updatedDetails.freedomNumber = data.freedomNumber.goal;
                updatedDetails.freedomNumberAchieved = data.freedomNumber.achieved;
            }
            if (data.thirtyDayInception) {
                updatedDetails.thirtyDayInceptionGoal = data.thirtyDayInception.goal;
                updatedDetails.thirtyDayInceptionAchieved = data.thirtyDayInception.achieved;
            }
            if (data.starvingCrowd !== undefined) {
                updatedDetails.starvingCrowdIdentified = data.starvingCrowd;
            }
            if (data.ruleOf100) {
                updatedDetails.ruleOf100DailyGoal = data.ruleOf100.dailyGoal;
                updatedDetails.ruleOf100AchievedToday = data.ruleOf100.achievedToday;
            }
            return await this.saveClientDetails(updatedDetails);
        } catch (err: any) {
            return { success: false, error: err.message || 'Failed to save internal activities' };
        }
    },

    async getFinancialDetails(email: string): Promise<{
        quotes: any[];
        invoices: any[];
        payments: any[];
        refunds: any[];
        credits: any[];
        ltv: number;
        purchasedProductsServices: any[];
        sharedFolderUrl: string;
        lastActive: string;
        referredBy: string;
        referrals: any[];
        partnerStatus: string;
        partnerType: string;
        commissionDue: number;
        commissionEarnedToDate: number;
        satisfactionScore: number;
        testimonialReviewCaseStudy: string;
    }> {
        try {
            const customers = await syncManager.fetchTabData('Customers', null, true);
            if (!Array.isArray(customers)) {
                return this.getEmptyFinancialDetails();
            }
            const customer = customers.find((r: any) =>
                r.Email === email || r.Customer_Email === email
            );
            if (!customer) {
                return this.getEmptyFinancialDetails();
            }
            return {
                quotes: customer.Quotes || [],
                invoices: customer.Invoices || [],
                payments: customer.Payments || [],
                refunds: customer.Refunds || [],
                credits: customer.Credits || [],
                ltv: parseFloat(customer.Lifetime_Value || customer.LTV || '0') || 0,
                purchasedProductsServices: customer.Purchased_Products_Services || [],
                sharedFolderUrl: customer.Shared_Folder_URL || customer.GitHub_Folder_Url || '',
                lastActive: customer.Last_Active || customer.Last_Interaction || '',
                referredBy: customer.Referred_By || '',
                referrals: customer.Referrals || [],
                partnerStatus: (customer.Partner_Status || 'none').toLowerCase(),
                partnerType: customer.Partner_Type || '',
                commissionDue: parseFloat(customer.Commission_Due || '0') || 0,
                commissionEarnedToDate: parseFloat(customer.Commission_Earned_To_Date || '0') || 0,
                satisfactionScore: parseInt(customer.Satisfaction_Score || '0') || 0,
                testimonialReviewCaseStudy: customer.Testimonial_Review_Case_Study || ''
            };
        } catch (err) {
            console.error('[CJMS API] Error fetching financial details:', err);
            return this.getEmptyFinancialDetails();
        }
    },

    getEmptyFinancialDetails() {
        return {
            quotes: [],
            invoices: [],
            payments: [],
            refunds: [],
            credits: [],
            ltv: 0,
            purchasedProductsServices: [],
            sharedFolderUrl: '',
            lastActive: '',
            referredBy: '',
            referrals: [],
            partnerStatus: 'none',
            partnerType: '',
            commissionDue: 0,
            commissionEarnedToDate: 0,
            satisfactionScore: 0,
            testimonialReviewCaseStudy: ''
        };
    },

    async getClientDetails(email: string): Promise<Partial<CJMSClient>> {
        try {
            const customer = (await syncManager.fetchTabData('Customers', null, true)).find(
                (r: any) => r.Email === email || r.Customer_Email === email
            );
            if (!customer) {return {};}
            return {
                company: customer.Company || undefined,
                sharedFolderUrl: customer.Shared_Folder_URL || customer.GitHub_Folder_Url || '',
                lastActive: customer.Last_Active || customer.Last_Interaction || '',
                referredBy: customer.Referred_By || '',
                referrals: customer.Referrals || [],
                partnerStatus: (customer.Partner_Status || 'none').toLowerCase(),
                partnerType: customer.Partner_Type || '',
                commissionDue: parseFloat(customer.Commission_Due || '0') || 0,
                commissionEarnedToDate: parseFloat(customer.Commission_Earned_To_Date || '0') || 0,
                satisfactionScore: parseInt(customer.Satisfaction_Score || '0') || 0,
                testimonialReviewCaseStudy: customer.Testimonial_Review_Case_Study || ''
            };
        } catch (err) {
            console.error('[CJMS API] Error fetching client details:', err);
            return {};
        }
    },

    async saveClientDetails(data: {
        email: string;
        company?: string;
        sharedFolderUrl?: string;
        lastActive?: string;
        referredBy?: string;
        referrals?: Array<{id: string; name: string; email: string; date: string; status: 'contacted' | 'qualified' | 'converted'}>;
        partnerStatus?: 'none' | 'referral' | 'affiliate' | 'reseller' | 'strategic';
        partnerType?: string;
        commissionDue?: number;
        commissionEarnedToDate?: number;
        satisfactionScore?: number;
        testimonialReviewCaseStudy?: string;
        freedomNumber?: number;
        freedomNumberAchieved?: number;
        thirtyDayInceptionGoal?: number;
        thirtyDayInceptionAchieved?: number;
        starvingCrowdIdentified?: boolean;
        ruleOf100DailyGoal?: number;
        ruleOf100AchievedToday?: number;
    }): Promise<{ success: boolean; error?: string }> {
        try {
            const customers = await syncManager.fetchTabData('Customers', null, true);
            if (!Array.isArray(customers)) {
                return { success: false, error: 'Customers table not found' };
            }

            const customerIndex = customers.findIndex((r: any) =>
                r.Email === data.email || r.Customer_Email === data.email
            );

            if (customerIndex >= 0) {
                const updatedCustomer = {
                    ...customers[customerIndex],
                    Company: data.company || customers[customerIndex].Company,
                    Shared_Folder_URL: data.sharedFolderUrl || customers[customerIndex].Shared_Folder_URL,
                    GitHub_Folder_Url: data.sharedFolderUrl || customers[customerIndex].GitHub_Folder_Url,
                    Last_Active: data.lastActive || customers[customerIndex].Last_Active,
                    Last_Interaction: data.lastActive || customers[customerIndex].Last_Interaction,
                    Referred_By: data.referredBy || customers[customerIndex].Referred_By,
                    Referrals: data.referrals || customers[customerIndex].Referrals,
                    Partner_Status: data.partnerStatus || customers[customerIndex].Partner_Status,
                    Partner_Type: data.partnerType || customers[customerIndex].Partner_Type,
                    Commission_Due: data.commissionDue !== undefined ? String(data.commissionDue) : customers[customerIndex].Commission_Due,
                    Commission_Earned_To_Date: data.commissionEarnedToDate !== undefined ? String(data.commissionEarnedToDate) : customers[customerIndex].Commission_Earned_To_Date,
                    Satisfaction_Score: data.satisfactionScore !== undefined ? String(data.satisfactionScore) : customers[customerIndex].Satisfaction_Score,
                    Testimonial_Review_Case_Study: data.testimonialReviewCaseStudy || customers[customerIndex].Testimonial_Review_Case_Study,
                    // Also save lead-metrics if they exist on customer record
                    Freedom_Number: data.freedomNumber !== undefined ? data.freedomNumber : customers[customerIndex].Freedom_Number,
                    Freedom_Number_Achieved: data.freedomNumberAchieved !== undefined ? data.freedomNumberAchieved : customers[customerIndex].Freedom_Number_Achieved,
                    Thirty_Day_Inception_Goal: data.thirtyDayInceptionGoal !== undefined ? data.thirtyDayInceptionGoal : customers[customerIndex].Thirty_Day_Inception_Goal,
                    Thirty_Day_Inception_Achieved: data.thirtyDayInceptionAchieved !== undefined ? data.thirtyDayInceptionAchieved : customers[customerIndex].Thirty_Day_Inception_Achieved,
                    Starving_Crowd_Identified: data.starvingCrowdIdentified !== undefined ? data.starvingCrowdIdentified : customers[customerIndex].Starving_Crowd_Identified,
                    Rule_Of_100_Daily_Goal: data.ruleOf100DailyGoal !== undefined ? data.ruleOf100DailyGoal : customers[customerIndex].Rule_Of_100_Daily_Goal,
                    Rule_Of_100_Achieved_Today: data.ruleOf100AchievedToday !== undefined ? data.ruleOf100AchievedToday : customers[customerIndex].Rule_Of_100_Achieved_Today,
                };
                await syncManager.logAction('Customers', 'update', updatedCustomer, String(customerIndex));
                return { success: true };
            }

            // Try CRM_Leads
            const leads = await syncManager.fetchTabData('CRM_Leads', null, true);
            if (!Array.isArray(leads)) {return { success: false, error: 'Leads table not found' };}

            const leadIndex = leads.findIndex((r: any) => r.Email === data.email);
            if (leadIndex < 0) {return { success: false, error: 'Client not found in either table' };}

            const updatedLead = {
                ...leads[leadIndex],
                Company: data.company || leads[leadIndex].Company,
                Freedom_Number: data.freedomNumber !== undefined ? data.freedomNumber : leads[leadIndex].Freedom_Number,
                Freedom_Number_Achieved: data.freedomNumberAchieved !== undefined ? data.freedomNumberAchieved : leads[leadIndex].Freedom_Number_Achieved,
                Thirty_Day_Inception_Goal: data.thirtyDayInceptionGoal !== undefined ? data.thirtyDayInceptionGoal : leads[leadIndex].Thirty_Day_Inception_Goal,
                Thirty_Day_Inception_Achieved: data.thirtyDayInceptionAchieved !== undefined ? data.thirtyDayInceptionAchieved : leads[leadIndex].Thirty_Day_Inception_Achieved,
                Starving_Crowd_Identified: data.starvingCrowdIdentified !== undefined ? data.starvingCrowdIdentified : leads[leadIndex].Starving_Crowd_Identified,
                Rule_Of_100_Daily_Goal: data.ruleOf100DailyGoal !== undefined ? data.ruleOf100DailyGoal : leads[leadIndex].Rule_Of_100_Daily_Goal,
                Rule_Of_100_Achieved_Today: data.ruleOf100AchievedToday !== undefined ? data.ruleOf100AchievedToday : leads[leadIndex].Rule_Of_100_Achieved_Today,
                Updated_At: new Date().toISOString(),
            };
            await syncManager.logAction('CRM_Leads', 'update', updatedLead, String(leadIndex));
            return { success: true };
        } catch (err: any) {
            return { success: false, error: err.message || 'Failed to save client details' };
        }
    },

    async fetchSuggestedScript(email: string): Promise<string> {
        try {
            const clients = await this.fetchClients();
            const client = clients.find(c => c.email === email);
            if (!client) {return `Client with email ${email} not found.`;}
            const history = await fetchCommunicationHistory(email, client.phone, client.id.replace(/^(customer_|lead_)/, ''));
            const recentContext = history.slice(0, 5).map(h =>
                `[${h.date}] ${h.direction === 'outgoing' ? 'You' : 'Client'} (${h.channel}): ${h.content}`
            ).join('\n');
            const prompt = `You are a BizBuilder sales coach. ${client.name} is a ${client.source} in the "${client.currentStage}" journey stage.

    Last communication: ${client.lastCommunication} — ${client.lastCommunicationContent || 'No recent messages'}
    Next action needed: ${client.nextAction}

    Recent communication context:
    ${recentContext || 'No previous conversations found.'}

    Write a helpful, professional WhatsApp message to ${client.name} that:
    1. References the client's stage (${client.currentStage}) in a natural way
    2. Moves the conversation forward based on the recent context
    3. Is conversational and warm — use the client's first name naturally
    4. Keeps it under 200 words`;
            const response = await ollamaService.generateText(prompt, 'llama3');
            return response || 'AI response unavailable';
        } catch (err) {
            return 'Failed to generate suggested script. Please try again later.';
        }
    },

    // ─── Commission API Endpoints ───────────────────────────────────────────────────────

    async requestPayout(data: { email: string; amount: number }): Promise<{ success: boolean; error?: string }> {
        try {
            const clients = await this.fetchClients();
            const client = clients.find(c => c.email === data.email);
            if (!client) {
                return { success: false, error: 'Client not found' };
            }

            const currentDue = client.commissionDue || 0;
            if (data.amount > currentDue) {
                return { success: false, error: 'Insufficient commission balance' };
            }

            const payoutEntry = {
                Payout_ID: `payout_${Date.now()}`,
                Date: new Date().toISOString(),
                Amount: data.amount.toFixed(2),
                Status: 'pending',
                Partner_Name: client.name,
                Partner_Email: client.email,
                Account_Details: 'PayFast Account (To be linked)'
            };

            await syncManager.logAction('Payouts', 'add', payoutEntry);

            return { success: true };
        } catch (err) {
            console.error('[CJMS API] Error requesting payout:', err);
            return { success: false, error: 'Failed to request payout' };
        }
    },

    async getReferrals(email: string): Promise<{ success: boolean; referrals?: any[] }> {
        try {
            const clients = await this.fetchClients();
            const client = clients.find(c => c.email === email);
            if (!client) {
                return { success: false, referrals: [] };
            }

            const referrals = (client.referrals || []).map((r: any, idx: number) => ({
                id: `referral_${idx + 1}`,
                referralCode: `RFR-${String(idx + 1).padStart(4, '0')}`,
                name: r.name || `Referral ${idx + 1}`,
                email: r.email || 'N/A',
                status: r.status || 'pending',
                createdAt: r.date || new Date().toISOString()
            }));

            return { success: true, referrals };
        } catch (err) {
            console.error('[CJMS API] Error fetching referrals:', err);
            return { success: false, referrals: [] };
        }
    },

    // Update a client's journey stage. The clientId encodes its source
    // (e.g. "lead_123" or "customer_456") so we know which tab to write to.
    async updateClientStage(clientId: string, nextStage: string): Promise<{ success: boolean; error?: string }> {
        try {
            const isLead = clientId.startsWith('lead_');
            const tab = isLead ? 'CRM_Leads' : 'Customers';
            const rows = await syncManager.fetchTabData(tab, null, true);
            if (!Array.isArray(rows)) {return { success: false, error: `${tab} table not found` };}

            const rowIdField = isLead ? 'Lead_ID' : 'Customer_ID';
            const bareId = clientId.replace(/^(lead_|customer_)/, '');
            const idx = rows.findIndex((r: any) =>
                String(r[rowIdField] || r.id || '') === bareId ||
                String(r[rowIdField] || r.id || '') === clientId
            );
            if (idx < 0) {return { success: false, error: 'Client row not found' };}

            const updated = {
                ...rows[idx],
                ...(isLead
                    ? { Status: nextStage, Last_Contact_Date: new Date().toISOString() }
                    : { Lifecycle_Stage: nextStage, Journey_Stage: nextStage, Last_Interaction: new Date().toISOString() }),
                Updated_At: new Date().toISOString(),
            };
            await syncManager.logAction(tab, 'update', updated, String(idx));
            return { success: true };
        } catch (err: any) {
            return { success: false, error: err.message || 'Failed to update stage' };
        }
    },

    // Add a new lead (thin wrapper around addClient with source='lead').
    async addLead(data: {
        firstName: string;
        lastName: string;
        email: string;
        phone?: string;
        company?: string;
        source?: string;
        notes?: string;
    }): Promise<{ success: boolean; error?: string }> {
        return this.addClient({ ...data, source: 'lead' });
    },

    // Save BANT qualification scores for a lead
    async saveBantScores(data: {
        leadId: string;
        email: string;
        budget: number;
        authority: number;
        need: number;
        timeline: number;
        notes?: string;
    }): Promise<{ success: boolean; totalScore?: number; qualificationStatus?: string; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/qualification/bant`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to save BANT scores' };
            }
            return { 
                success: true, 
                totalScore: result.totalScore, 
                qualificationStatus: result.qualificationStatus 
            };
        } catch (err: any) {
            console.error('[CJMS API] Failed to save BANT scores:', err);
            return { success: false, error: err.message || 'Failed to save BANT scores' };
        }
    },

    // Log a touchpoint interaction
    async addTouchpoint(data: {
        leadId: string;
        email: string;
        channel: string;
        type?: string;
        notes?: string;
        outcome?: string;
    }): Promise<{ success: boolean; touchCount?: number; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/touchpoint/log`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to log touchpoint' };
            }
            return { success: true, touchCount: result.touchCount };
        } catch (err: any) {
            console.error('[CJMS API] Failed to log touchpoint:', err);
            return { success: false, error: err.message || 'Failed to log touchpoint' };
        }
    },

    // Schedule a discovery call
    async scheduleDiscoveryCall(data: {
        leadId: string;
        email: string;
        date: string;
        time: string;
        participants?: string;
        notes?: string;
    }): Promise<{ success: boolean; scheduled?: { date: string; time: string; participants?: string }; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/discovery/schedule`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to schedule discovery call' };
            }
            return { success: true, scheduled: result.scheduled };
        } catch (err: any) {
            console.error('[CJMS API] Failed to schedule discovery call:', err);
            return { success: false, error: err.message || 'Failed to schedule discovery call' };
        }
    },

    // Update lead qualification status
    async updateQualificationStatus(data: {
        leadId: string;
        email?: string;
        newStatus: string;
        notes?: string;
    }): Promise<{ success: boolean; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/qualification/status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to update status' };
            }
            return { success: true };
        } catch (err: any) {
            console.error('[CJMS API] Failed to update qualification status:', err);
            return { success: false, error: err.message || 'Failed to update qualification status' };
        }
    },

    // Get pending leads for admin review queue
    async getPendingQualifications(): Promise<{ success: boolean; leads?: any[]; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/qualification/pending`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to fetch pending qualifications' };
            }
            return { success: true, leads: result.leads };
        } catch (err: any) {
            console.error('[CJMS API] Failed to fetch pending qualifications:', err);
            return { success: false, error: err.message || 'Failed to fetch pending qualifications' };
        }
    },

    // ========================================================================
    // STAGE 3: ASSESSMENT API
    // ========================================================================

    async submitAssessment(data: {
        leadId: string;
        email: string;
        currentRevenue?: string;
        customerCount?: string;
        averageTransactionValue?: string;
        sixMonthTarget?: string;
        freedomNumber?: string;
        additionalCustomersNeeded?: string;
        idealCustomerLifetimeValue?: string;
        websiteProblem?: string;
        visitorOutcome?: string;
        keyMessage?: string;
        callToAction?: string;
        inspirationWebsites?: string;
        brandPersonality?: string;
        colorPreferences?: string;
        industry?: string;
        businessName?: string;
    }): Promise<{ success: boolean; assessment?: any; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/assessment/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to submit assessment' };
            }
            return { success: true, assessment: result.assessment };
        } catch (err: any) {
            console.error('[CJMS API] Failed to submit assessment:', err);
            return { success: false, error: err.message || 'Failed to submit assessment' };
        }
    },

    async getAssessmentResults(email: string): Promise<{ success: boolean; assessment?: any; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/assessment/results/${encodeURIComponent(email)}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to fetch assessment results' };
            }
            return { success: true, assessment: result.assessment };
        } catch (err: any) {
            console.error('[CJMS API] Failed to fetch assessment results:', err);
            return { success: false, error: err.message || 'Failed to fetch assessment results' };
        }
    },

    // ========================================================================
    // STAGE 4: PRODUCT SELECTION
    // ========================================================================

    async chooseProgram(data: {
        leadId: string;
        email: string;
        programId: string;
        programName: string;
        price: number;
    }): Promise<{ success: boolean; selection?: any; newStatus?: string; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/selection/choose`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to record program selection' };
            }
            return { success: true, selection: result.selection, newStatus: result.newStatus };
        } catch (err: any) {
            console.error('[CJMS API] Failed to record program selection:', err);
            return { success: false, error: err.message || 'Failed to record program selection' };
        }
    },

    async processPayment(data: {
        leadId: string;
        email: string;
        programId: string;
        amount: number;
    }): Promise<{ success: boolean; payment?: any; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/payment/mock`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to process payment' };
            }
            return { success: true, payment: result.payment };
        } catch (err: any) {
            console.error('[CJMS API] Failed to process payment:', err);
            return { success: false, error: err.message || 'Failed to process payment' };
        }
    },

    // ========================================================================
    // STAGE 5: ONBOARDING & 1-PAGER WEBSITE DELIVERY
    // ========================================================================

    async submitQuestionnaire(data: {
        leadId: string;
        email: string;
        responses: Record<string, any>;
    }): Promise<{ success: boolean; provisioningStep?: string; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/onboarding/questionnaire`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to submit questionnaire' };
            }
            return { success: true, provisioningStep: result.provisioningStep };
        } catch (err: any) {
            console.error('[CJMS API] Failed to submit questionnaire:', err);
            return { success: false, error: err.message || 'Failed to submit questionnaire' };
        }
    },

    async generateWebsite(data: {
        leadId: string;
        email: string;
    }): Promise<{ success: boolean; previewUrl?: string; provisioningStep?: string; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/onboarding/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to generate website' };
            }
            return { success: true, previewUrl: result.previewUrl, provisioningStep: result.provisioningStep };
        } catch (err: any) {
            console.error('[CJMS API] Failed to generate website:', err);
            return { success: false, error: err.message || 'Failed to generate website' };
        }
    },

    async reviewWebsite(data: {
        leadId: string;
        email: string;
        approved: boolean;
        feedback?: string;
    }): Promise<{ success: boolean; provisioningStep?: string; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/onboarding/review`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to submit review' };
            }
            return { success: true, provisioningStep: result.provisioningStep };
        } catch (err: any) {
            console.error('[CJMS API] Failed to submit review:', err);
            return { success: false, error: err.message || 'Failed to submit review' };
        }
    },

    async approveWebsite(data: {
        leadId: string;
        email: string;
        approved: boolean;
        revisionNotes?: string;
    }): Promise<{ success: boolean; provisioningStep?: string; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/onboarding/approve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to submit approval' };
            }
            return { success: true, provisioningStep: result.provisioningStep };
        } catch (err: any) {
            console.error('[CJMS API] Failed to submit approval:', err);
            return { success: false, error: err.message || 'Failed to submit approval' };
        }
    },

    async deployWebsite(data: {
        leadId: string;
        email: string;
    }): Promise<{ success: boolean; liveUrl?: string; provisioningStep?: string; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/onboarding/deploy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to deploy website' };
            }
            return { success: true, liveUrl: result.liveUrl, provisioningStep: result.provisioningStep };
        } catch (err: any) {
            console.error('[CJMS API] Failed to deploy website:', err);
            return { success: false, error: err.message || 'Failed to deploy website' };
        }
    },

    async updateProvisioningStep(data: {
        leadId: string;
        email: string;
        step: string;
    }): Promise<{ success: boolean; provisioningStep?: string; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/onboarding/step`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to update step' };
            }
            return { success: true, provisioningStep: result.provisioningStep };
        } catch (err: any) {
            console.error('[CJMS API] Failed to update provisioning step:', err);
            return { success: false, error: err.message || 'Failed to update provisioning step' };
        }
    },

    // ========================================================================
    // STAGE 7: GROWTH & SCALING TOOLS
    // ========================================================================

    async getGrowthTools(email: string): Promise<{ success: boolean; tools?: any; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/growth/tools?email=${encodeURIComponent(email)}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to fetch growth tools' };
            }
            return { success: true, tools: result.tools };
        } catch (err: any) {
            console.error('[CJMS API] Failed to fetch growth tools:', err);
            return { success: false, error: err.message || 'Failed to fetch growth tools' };
        }
    },

    async scheduleQuarterlyReview(data: {
        leadId?: string;
        email: string;
        nextReview?: string;
    }): Promise<{ success: boolean; nextReview?: string; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/growth/quarterly-review`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to schedule review' };
            }
            return { success: true, nextReview: result.nextReview };
        } catch (err: any) {
            console.error('[CJMS API] Failed to schedule review:', err);
            return { success: false, error: err.message || 'Failed to schedule review' };
        }
    },

    // ========================================================================
    // STAGE 8: COMMISSION & PARTNER SYSTEM
    // ========================================================================

    async getCommissionBalance(email: string): Promise<{ success: boolean; balance?: number; pending?: number; lifetimeEarnings?: number; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/commission?email=${encodeURIComponent(email)}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to fetch commission balance' };
            }
            return { success: true, balance: result.balance, pending: result.pending, lifetimeEarnings: result.lifetimeEarnings };
        } catch (err: any) {
            console.error('[CJMS API] Failed to fetch commission balance:', err);
            return { success: false, error: err.message || 'Failed to fetch commission balance' };
        }
    },

    async calculateCommission(data: {
        partnerEmail: string;
        partnerDeliveredSales: number;
    }): Promise<{ success: boolean; commissionAmount?: string; ledgerId?: string; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/commission/calculate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to calculate commission' };
            }
            return { success: true, commissionAmount: result.commissionAmount, ledgerId: result.ledgerId };
        } catch (err: any) {
            console.error('[CJMS API] Failed to calculate commission:', err);
            return { success: false, error: err.message || 'Failed to calculate commission' };
        }
    },

    async recordPayout(data: {
        ledgerId: string;
        payoutMethod?: string;
        payoutDate?: string;
    }): Promise<{ success: boolean; status?: string; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/commission/payout`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to record payout' };
            }
            return { success: true, status: result.status };
        } catch (err: any) {
            console.error('[CJMS API] Failed to record payout:', err);
            return { success: false, error: err.message || 'Failed to record payout' };
        }
    },

    async getCommissionHistory(email: string): Promise<{ success: boolean; history?: any[]; totalEarned?: number; totalPaid?: number; pending?: number; error?: string }> {
        try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za';
            const response = await fetch(`${apiBase}/commission/history?email=${encodeURIComponent(email)}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Failed to fetch commission history' };
            }
            return { success: true, history: result.history, totalEarned: result.totalEarned, totalPaid: result.totalPaid, pending: result.pending };
        } catch (err: any) {
            console.error('[CJMS API] Failed to fetch commission history:', err);
            return { success: false, error: err.message || 'Failed to fetch commission history' };
        }
    },

    // ─── Client Review Methods (used by ClientReviewInterface) ────────────

    async getClientReviews(clientId: string): Promise<{ success: boolean; reviews?: any[]; error?: string }> {
        try {
            const data = await syncManager.getTabData('CRM_Leads');
            const reviews = data
                .filter((row: any) => row['Project_ID'] === clientId || row['Client_ID'] === clientId)
                .map((row: any) => ({
                    id: row['Project_ID'] || row['Client_ID'] || '',
                    clientName: row['Business_Name'] || 'Unknown',
                    businessType: row['Business_Type'] || '',
                    websiteUrl: row['Website_URL'] || '',
                    previewImage: row['Preview_Image'] || '',
                    status: row['Review_Status'] || 'assessment_pending',
                    referralProgress: Number(row['Referral_Progress']) || 0,
                    commissionRate: Number(row['Commission_Rate']) || 0,
                    commissionEarned: Number(row['Commission_Earned']) || 0,
                    commissionDue: Number(row['Commission_Due']) || 0,
                    feedback: row['Feedback'] || '',
                    version: Number(row['Version']) || 1,
                    createdAt: row['Created_At'] || new Date().toISOString(),
                    updatedAt: row['Updated_At'] || new Date().toISOString(),
                }));
            return { success: true, reviews };
        } catch (err: any) {
            console.error('[CJMS API] Failed to fetch client reviews:', err);
            return { success: false, error: err.message || 'Failed to fetch client reviews' };
        }
    },

    async saveReviewComments(reviewId: string, comments: any[]): Promise<{ success: boolean; error?: string }> {
        try {
            await syncManager.updateTabData('CRM_Leads', {
                Project_ID: reviewId,
                Review_Comments: JSON.stringify(comments),
                Updated_At: new Date().toISOString(),
            });
            return { success: true };
        } catch (err: any) {
            console.error('[CJMS API] Failed to save review comments:', err);
            return { success: false, error: err.message || 'Failed to save review comments' };
        }
    },

    async requestRevision(reviewId: string, feedback: string): Promise<{ success: boolean; error?: string }> {
        try {
            await syncManager.updateTabData('CRM_Leads', {
                Project_ID: reviewId,
                Review_Status: '1_pager_review',
                Feedback: feedback,
                Version: { __increment: 1 },
                Updated_At: new Date().toISOString(),
            });
            return { success: true };
        } catch (err: any) {
            console.error('[CJMS API] Failed to request revision:', err);
            return { success: false, error: err.message || 'Failed to request revision' };
        }
    },

    async approveReview(reviewId: string): Promise<{ success: boolean; error?: string }> {
        try {
            await syncManager.updateTabData('CRM_Leads', {
                Project_ID: reviewId,
                Review_Status: 'completed',
                Updated_At: new Date().toISOString(),
            });
            return { success: true };
        } catch (err: any) {
            console.error('[CJMS API] Failed to approve review:', err);
            return { success: false, error: err.message || 'Failed to approve review' };
        }
    },

    async completeReview(reviewId: string): Promise<{ success: boolean; error?: string }> {
        try {
            await syncManager.updateTabData('CRM_Leads', {
                Project_ID: reviewId,
                Review_Status: 'completed',
                Completed_At: new Date().toISOString(),
                Updated_At: new Date().toISOString(),
            });
            return { success: true };
        } catch (err: any) {
            console.error('[CJMS API] Failed to complete review:', err);
            return { success: false, error: err.message || 'Failed to complete review' };
        }
    },
};
